/**
 * Canvas-based crop extraction for react-easy-crop's pixel-crop output.
 * Downscales to `outputSize` (capped — never upscales past the source
 * image's own resolution) so the exported file stays reasonably small
 * regardless of the original upload's dimensions, and works identically
 * whether the crop happens on a desktop browser or inside the mobile app.
 */
export function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (e) => reject(e));
    img.crossOrigin = "anonymous";
    img.src = src;
  });
}

export async function getCroppedImageBlob(imageSrc, pixelCrop, { outputSize = 1200, mimeType = "image/jpeg", quality = 0.92 } = {}) {
  const image = await loadImage(imageSrc);
  // Scale the longer edge down to outputSize, never upscale past the
  // crop's own resolution — keeps the aspect ratio the user actually
  // selected (1:1 for products, but generalized for any future aspect).
  const scale = Math.min(1, outputSize / Math.max(pixelCrop.width, pixelCrop.height));
  const outW = Math.round(pixelCrop.width * scale);
  const outH = Math.round(pixelCrop.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingQuality = "high";

  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, outW, outH,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) { reject(new Error("Canvas is empty")); return; }
      resolve(blob);
    }, mimeType, quality);
  });
}
